<template>
  <div id="ls-conversation-screen">
    <div
      v-if="!conversation.loadingStatus.isLoading && areThereContractors"
      class="ls-conversation-screen--main"
    >
      <div class="ls-conversation-screen--header">
        <LsAddressCard v-if="currentProperty && !screenSize.desktop" :property="currentProperty" />
        <div>
          <div
            v-if="!screenSize.desktop"
            class="ls-conversation-screen--back-wrapper"
            test-id="back"
            @click="goBack"
          >
            <div class="ls-conversation-screen--chevron"><LsIcon type="chevron" size="md" /></div>
            <span class="ls-conversation-screen--back">BACK</span>
          </div>

          <h1 class="ls-conversation-screen--name has-text-weight-bold" test-id="contractor_name">
            {{ contractorName }}
          </h1>
          <p
            v-if="screenSize.desktop"
            class="ls-conversation-screen--business-name"
            test-id="business_name"
          >
            {{
              conversation.currentConversation.contractor
                ? conversation.currentConversation.contractor.name
                : ''
            }}
          </p>
        </div>
      </div>
      <div :class="['ls-conversation-screen--messages-wrapper', { columns: screenSize.desktop }]">
        <div :class="{ column: screenSize.desktop }">
          <div
            id="ls-conversation-screen--messages"
            :class="[
              'ls-conversation-screen--messages',
              { 'is-property-manager': currentProperty },
            ]"
          >
            <div v-if="localMessages.length">
              <ConversationMessage
                v-for="(message, idx) in localMessages"
                :key="message.message_id"
                :message="message"
                :is-last="messageBeingSaved ? false : idx === localMessages.length - 1"
              />
              <ConversationMessage
                v-if="messageBeingSaved"
                id="ls-temp-message"
                :message="messageBeingSaved"
                is-last
              />
            </div>
            <div v-else class="ls-conversation-screen--placholder">
              <p
                v-if="conversation.currentConversation.is_active"
                class="ls-conversation-screen--placholder-text"
              >
                This is the beginning of your conversation with
                {{ conversation.currentConversation.contractor.user.first_name }}.
              </p>
            </div>
          </div>
          <div
            v-if="!conversation.currentConversation.is_active"
            class="ls-conversation-screen--archived"
          >
            <span class="has-text-weight-bold"
              >You have no active subscriptions with this crew.</span
            >
            This conversation has been archived.
          </div>
          <form novalidate @submit.prevent="sendMessage">
            <div class="ls-conversation-screen--input-wrapper">
              <div class="column is-9">
                <LsInput
                  v-model="messageText"
                  track-id="ls_conversation_screen-message"
                  name="message"
                  placeholder="Write a message..."
                  :type="screenSize.desktop ? 'textarea' : 'text'"
                  :disabled="!conversation.currentConversation.is_active"
                />
              </div>
              <div class="ls-conversation-screen--button-wrapper">
                <LsButton
                  test-id="button"
                  track-id="ls_conversation_screen-send_message"
                  bg-color="primary"
                  text="send message"
                  :disabled="!messageText.length"
                  type="submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="!areThereContractors" class="ls-conversation-screen--placholder">
      <p class="ls-conversation-screen--placholder-text">There are no current conversations.</p>
    </div>
    <BLoading :active.sync="conversation.loadingStatus.isLoading" :is-full-page="false" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ConversationMessage from '@/views/Authorized/InboxView/Conversation/ConversationMessage.vue';
import { LsInput, LsButton, LsIcon, LsAddressCard } from '@/components/common';
import { CONVERSATION__SEND_MESSAGE, CONVERSATION__UNDUPED_CONTACTS } from '@/store/types';
import moment from 'moment';
import VueScrollTo from 'vue-scrollto';
import _ from 'lodash';
import VueTypes from 'vue-types';
import { lsPropTypeHelpers } from 'ls-vue-common';

export default {
  name: 'ConversationMessages',
  components: { ConversationMessage, LsInput, LsButton, LsIcon, LsAddressCard },
  props: {
    currentProperty: VueTypes.custom(lsPropTypeHelpers.objectOrNull),
  },
  data() {
    return {
      messageText: '',
      messageBeingSaved: null,
    };
  },
  computed: {
    ...mapState(['conversation', 'screenSize']),
    ...mapGetters([CONVERSATION__UNDUPED_CONTACTS]),

    areThereContractors() {
      return Boolean(this[CONVERSATION__UNDUPED_CONTACTS].length + this.conversations.length);
    },
    localMessages() {
      return this.conversation.currentConversation.messages;
    },
    contractorName() {
      if (this.conversation.currentConversation.contractor) {
        return `${this.conversation.currentConversation.contractor.user.first_name} ${
          this.conversation.currentConversation.contractor.user.last_name
        }`;
      }
      return '';
    },
    conversations() {
      return this.conversation.conversations;
    },
  },
  watch: {
    conversation: {
      handler() {
        if (!this.conversation.loadingStatus.isLoading) {
          this.scrollToBottom();
        }
      },
      // if watching anything beyond first level of object, need deep
      deep: true,
    },
  },
  mounted() {
    if (!this.conversation.loadingStatus.isLoading && this.conversation.conversations.length) {
      this.scrollToBottom();
    }
  },
  methods: {
    ...mapActions([CONVERSATION__SEND_MESSAGE]),
    goBack() {
      this.$router.replace({
        name: 'inbox',
      });
    },
    sendMessage() {
      if (!_.size(this.messageText)) {
        return;
      }
      const message = {
        text: this.messageText,
      };

      const messageBeingSaved = {
        ...message,
        // fake construction of message to work with component
        created_at: moment()
          .utc()
          .format('YYYY-MM-DD HH:mm:ss'),
        message_id: moment().valueOf(),
        // customer id just needs to be truthy
        user: { customer_id: 1 },
      };

      this.messageBeingSaved = messageBeingSaved;

      this[CONVERSATION__SEND_MESSAGE]({
        message: {
          text: this.messageText,
        },
      }).then(() => {
        this.messageBeingSaved = null;
      });

      this.messageText = '';
    },
    scrollToBottom() {
      if (this.localMessages.length) {
        // setTimeout needed to allow full height of div or body to load prior to scrolling

        setTimeout(() => {
          const messageDiv = this.screenSize.desktop
            ? document.getElementById('ls-conversation-screen--messages')
            : document.body;

          const html = document.documentElement;
          //caclulate the full height of the body if using body, but also works for the div.
          const height = Math.max(
            messageDiv.scrollHeight,
            messageDiv.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight
          );

          if (messageDiv) {
            VueScrollTo.scrollTo(messageDiv, {
              easing: 'ease-in-out',
              container: messageDiv,
              duration: 300,
              offset: height,
            });
          }
        }, 100);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#ls-conversation-screen {
  display: flex;
  align-items: center;
  height: 85vh;
  position: relative;
}

.ls-conversation-screen--main,
.ls-conversation-screen--messages-wrapper {
  height: 100%;
  width: 100%;
}

.ls-conversation-screen--header {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 0 20px 0;
  border-bottom: 2px solid $primary-light;

  > div:first-child {
    width: 100%;
    margin-bottom: 10px;
  }

  > div:last-child {
    width: 100%;
    justify-content: space-between;
    display: flex;
    align-items: baseline;
  }
}

.ls-conversation-screen--business-name {
  font-size: $size-5;
  padding-left: 10px;
  color: $primary;
}

.ls-conversation-screen--messages {
  overflow-y: scroll;
  overflow-x: hidden;
  height: 70%;
  width: 633px;
}

.ls-conversation-screen--messages::-webkit-scrollbar {
  width: 20px;
  background-color: $white;
}

.ls-conversation-screen--messages::-webkit-scrollbar-thumb {
  background-color: $gray-tertiary;
  border: 6px solid $white;
}

.ls-conversation-screen--placholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.ls-conversation-screen--placholder-text {
  text-align: center;
  font-size: $size-4;
  color: $primary;
}

.ls-conversation-screen--archived {
  font-size: $size-6;
  color: $white;
  padding: 10px;
  background-color: $tertiary;
}

.ls-conversation-screen--input-wrapper {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background-color: $primary-light;
}

.ls-conversation-screen--button-wrapper {
  padding-top: 10px;
}

@media (max-width: $mobile-max-width) {
  #ls-conversation-screen {
    display: flex;
    align-items: center;
  }

  .ls-conversation-screen--header {
    z-index: $z-index-conversation-header;
    user-select: none;
    background-color: $white;
    position: fixed;
    top: $navbar-height;
    left: 0;
    padding: 20px 20px 20px 15px;
    justify-content: space-between;
    align-items: center;
  }

  .ls-conversation-screen--back-wrapper {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .ls-conversation-screen--chevron {
    transform: rotate(180deg) translateY(2px);
  }

  .ls-conversation-screen--back {
    color: $primary;
    font-weight: bold;
    position: relative;
  }
  .ls-conversation-screen--name {
    color: $gray-primary;
    font-size: $size-4;
  }

  .ls-conversation-screen--messages {
    padding: 125px 0 160px 0;
    width: 100%;
    height: 100%;

    &.is-property-manager {
      margin-top: 80px;
    }
  }

  .ls-conversation-screen--placholder {
    padding-top: 30px;
  }

  .ls-conversation-screen--archived {
    position: fixed;
    bottom: 112px;
    left: 0;
    width: 100%;
  }

  .ls-conversation-screen--input-wrapper {
    display: block;
    position: fixed;
    bottom: 0px;
    left: 0px;
    width: 100%;
  }
}
.themed-app {
  .ls-conversation-screen--header {
    border-color: $theme-color-primary-light;
  }
  .ls-conversation-screen--business-name {
    font-size: $theme-txt-body;
    color: $theme-color-primary;
  }
  .ls-conversation-screen--messages::-webkit-scrollbar {
    background-color: $theme-color-white;
  }

  .ls-conversation-screen--messages::-webkit-scrollbar-thumb {
    background-color: $theme-color-gray-dark;
    border-color: $theme-color-white;
  }
  .ls-conversation-screen--placholder-text {
    font-size: $theme-txt-body-lg;
    color: $theme-color-primary;
  }
  .ls-conversation-screen--archived {
    font-size: $theme-txt-body-lg;
    color: $theme-color-white;
    background-color: $theme-color-secondary;
  }
  .ls-conversation-screen--input-wrapper {
    background-color: $theme-color-primary-light;
  }
  @media (max-width: $mobile-max-width) {
    .ls-conversation-screen--header {
      background-color: $theme-color-white;
    }
    .ls-conversation-screen--back {
      color: $theme-color-primary;
    }
    .ls-conversation-screen--name {
      color: $theme-color-gray-darker;
      font-size: $theme-txt-body-lg;
    }
  }
}
</style>
